/**
 * progressHero — the Charted Atlas hero's shared renderers.
 *
 * The `/discovery` Progress tab and the understanding-map right panel (opened
 * from the in-chat memory-update card) render the same hero: the aside
 * column beside the polar chart, over one topic row per topic. Both surfaces
 * must not drift, so the markup lives here once; each surface supplies its
 * own interaction wiring (`bindTopicRows`) and, on the Progress tab, the
 * Up-next block the panel deliberately omits.
 *
 * Data comes shaped by `core/progressData.js` (`loadTopicProgress`), which is
 * likewise shared by both surfaces.
 *
 * The row markup itself is one level lower still: `renderNavRow` emits the
 * single nav-row shape, and `renderTopicRow` / `renderPathRow` are thin
 * wrappers over it, so the Progress and Paths tabs cannot drift apart.
 * Likewise `renderHeroAside` under `renderProgressAside`.
 *
 * @module components/progressHero
 */

import { escapeHtml } from '../core/utils.js';
import { levelOf, levelLabel } from './depthProgress.js';
import { topicFill } from './topicPalette.js';
import { topicBlurbText } from '../core/progressData.js';
import { discOverlaysHtml } from './pathDisc.js';

/** Right chevron used by the rows' meta slot and the Up-next CTA. */
export const CHEVRON_SVG = `<svg width="19" height="19" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;

/**
 * Depth bands in ascending order — the ladder the stat line climbs. Values
 * are `levelOf` keys, so the thresholds themselves stay owned by
 * `depthProgress.js`.
 * @type {ReadonlyArray<string>}
 */
const BAND_LADDER = Object.freeze(['beginning', 'intermediate', 'deep']);

/**
 * The one stat line under the band word, chosen for where the user actually
 * is. Each rung reports the only count that can still move: while topics are
 * untouched, breadth is the news; once every topic is charted that number is
 * pinned at N of N and says nothing, so the line steps up to the next band
 * and starts counting that instead. Purely a function of current data — no
 * persisted phase flag — so it never needs resetting and can only ever move
 * forward.
 *
 * @param {Array<{progress: number}>} topics - Topic model
 * @returns {string|null} Stat sentence, or null with no topics
 */
export function progressStatLine(topics) {
  const total = topics.length;
  if (total === 0) return null;

  const atLeast = (band) => {
    const floor = BAND_LADDER.indexOf(band);
    return topics.filter(t => BAND_LADDER.indexOf(levelOf(t.progress)) >= floor).length;
  };

  const charted = atLeast('beginning');
  if (charted < total) return `${charted} of ${total} topics charted`;

  const beyond = atLeast('intermediate');
  if (beyond < total) return `${beyond} of ${total} past the surface`;

  const deep = atLeast('deep');
  if (deep < total) return `${deep} of ${total} explored deeply`;

  return `All ${total} topics explored deeply`;
}

/**
 * The hero aside, as raw slots: an optional eyebrow over a band word, an
 * optional stat line, an optional trusted block inside the state group (the
 * Paths tab's per-path facts), and an optional block beneath (the Progress
 * tab's Up-next; the right panel passes none and shows state alone).
 *
 * `eyebrow` is conditional the same way `stat` is — `.hero-state` is a grid,
 * so an empty span still consumes a row gap, which is wrong for the states
 * that carry no eyebrow at all.
 *
 * Escaping: `eyebrow`, `band` and `stat` are plain text and are escaped here.
 * `stateHtml` and `nextHtml` are trusted, pre-built HTML supplied by the caller.
 *
 * @param {Object} opts
 * @param {string} [opts.eyebrow=''] - Small label above the band; omitted entirely when empty
 * @param {string} opts.band - The large state word
 * @param {string} [opts.stat=''] - One stat sentence under the band; omitted when empty
 * @param {string} [opts.stateHtml=''] - Pre-built HTML appended inside `.hero-state`, after the stat
 * @param {string} [opts.nextHtml=''] - Pre-built block HTML rendered under `.hero-state`
 * @returns {string} HTML string
 */
export function renderHeroAside({ eyebrow = '', band, stat = '', stateHtml = '', nextHtml = '' }) {
  return `
    <div class="discovery-hero-aside">
      <div class="hero-state">
        ${eyebrow ? `<span class="hero-state__eyebrow">${escapeHtml(eyebrow)}</span>` : ''}
        <span class="hero-state__band">${escapeHtml(band)}</span>
        ${stat ? `<span class="hero-state__stat">${escapeHtml(stat)}</span>` : ''}
        ${stateHtml}
      </div>
      ${nextHtml}
    </div>
  `;
}

/**
 * The aside beside the map: where you are (band word over its adaptive stat
 * line) and, when the caller supplies one, what to do next (the Progress
 * tab's Up-next block; the right panel passes none and shows state alone).
 *
 * Depth distribution is deliberately absent — twelve identity-dotted rows and
 * the chart's radii already carry it, and as a tile it restated the band word
 * in a third format.
 *
 * @param {Array<{id: string, progress: number}>} topics - Topic model
 * @param {number} overall - API overall percent (0-100)
 * @param {Object} [opts]
 * @param {string} [opts.nextHtml=''] - Pre-built Up-next block HTML
 * @returns {string} HTML string
 */
export function renderProgressAside(topics, overall, { nextHtml = '' } = {}) {
  return renderHeroAside({
    eyebrow: 'Overall understanding',
    band: levelLabel(overall),
    stat: progressStatLine(topics) || '',
    nextHtml
  });
}

/**
 * The one nav-row markup both `/discovery` list surfaces render — topic rows
 * on the Progress tab, path rows on the Paths tab. Everything that differs
 * between them arrives as a parameter; the element order, class names and
 * whitespace live here once so the two surfaces cannot drift.
 *
 * **Escaping contract — the one thing that could introduce an XSS
 * regression.** This function escapes `id`, `title`, `summary` and `ariaLabel`
 * itself, so callers must pass those as *plain text*, never pre-escaped (they
 * would double-escape). Conversely `slotHtml`, `bodyHtml` and `metaHtml` are
 * interpolated verbatim: callers must pass HTML they built themselves, having
 * already escaped any user- or API-supplied text inside it.
 *
 * @param {Object} opts
 * @param {string} opts.rowClass - Surface class(es) after `note-row`, e.g.
 *   `discovery-topic-row` (may carry modifiers; `session-card--active` is added here)
 * @param {string} opts.idAttr - Identity data attribute, e.g. `data-topic-id`
 * @param {string} opts.id - Identity value (escaped here)
 * @param {string} opts.slotHtml - Inner HTML of `.session-card__slot` (trusted HTML)
 * @param {string} [opts.slotClass=''] - Extra class on the slot itself (the path
 *   rows pass `path-disc`); empty adds no markup at all
 * @param {string} opts.title - Row title as plain text (escaped here)
 * @param {string} opts.summary - Summary line as plain text (escaped here)
 * @param {string} [opts.summaryHtml=''] - Trusted, pre-escaped HTML for the
 *   summary line, used INSTEAD of `summary` when present. The escape hatch for
 *   a summary whose clauses are not all the same colour; callers that pass it
 *   own the escaping of everything inside it
 * @param {string} [opts.bodyHtml=''] - Trusted HTML slotted between
 *   `.session-card__title-row` and `.session-card__summary` (the progress bar's home)
 * @param {string} [opts.metaHtml=''] - Trusted HTML prepended inside
 *   `.note-row__meta`, before the chevron
 * @param {string} [opts.titleSuffixHtml=''] - Trusted HTML appended inside
 *   `.session-card__title-row`, immediately after the `<h4>` (e.g. a
 *   notification badge)
 * @param {boolean} [opts.isActive=false] - Adds `session-card--active` + `aria-expanded`
 * @param {string} opts.ariaLabel - Accessible name as plain text (escaped here)
 * @returns {string} HTML string
 */
function renderNavRow({ rowClass, idAttr, id, slotHtml, slotClass = '', title, summary, summaryHtml = '',
                        bodyHtml = '', metaHtml = '', titleSuffixHtml = '', isActive = false,
                        ariaLabel }) {
  const activeCls = isActive ? ' session-card--active' : '';
  const slotCls = slotClass ? ` ${slotClass}` : '';
  // Empty slots must add no whitespace at all — the topic row's markup is
  // pinned byte-for-byte (test/components/progressHero.rowContract.test.js).
  const bodySlot = bodyHtml ? `\n        ${bodyHtml}` : '';
  const metaSlot = metaHtml ? `\n        ${metaHtml}` : '';
  const titleSuffixSlot = titleSuffixHtml || '';

  return `
    <div class="card session-card session-card--iconed note-row ${rowClass}${activeCls}"
         ${idAttr}="${escapeHtml(id)}"
         role="button"
         tabindex="0"
         aria-expanded="${isActive}"
         aria-label="${escapeHtml(ariaLabel)}">
      <span class="session-card__slot${slotCls}">${slotHtml}</span>
      <div class="session-card__content">
        <div class="session-card__title-row">
          <h4 class="session-card__title">${escapeHtml(title)}</h4>${titleSuffixSlot}
        </div>${bodySlot}
        <p class="session-card__summary">${summaryHtml || escapeHtml(summary)}</p>
      </div>
      <span class="note-row__meta">${metaSlot}
        <span class="session-card__chevron note-row__chevron" aria-hidden="true">${CHEVRON_SVG}</span>
      </span>
    </div>
  `;
}

/**
 * One topic row, in the shared `/search` row idiom: identity dot in the icon
 * slot (the topic's full palette hue — a nameplate, never depth-tinted;
 * depth lives in the polar chart), topic label over its teaser blurb, and an
 * always-visible chevron in the meta slot. The row carries no band word, but
 * the aria-label keeps it so it is still announced.
 *
 * @param {Object} topic - Topic as `loadTopicProgress` shapes it
 * @param {Map<string, Object>|null} blurbs - Topic-summary map keyed by topic id
 * @param {Object} [opts]
 * @param {string|null} [opts.activeTopicId=null] - Topic whose detail is
 *   currently open (adds `session-card--active` + `aria-expanded`)
 * @returns {string} HTML string
 */
export function renderTopicRow(topic, blurbs, { activeTopicId = null } = {}) {
  const blurb = topicBlurbText(topic, blurbs);
  const band = levelLabel(topic.progress);

  return renderNavRow({
    rowClass: 'discovery-topic-row',
    idAttr: 'data-topic-id',
    id: topic.id,
    slotHtml: `<span class="topic-dot" style="background:${topicFill(topic.id)}"></span>`,
    title: topic.label,
    summary: blurb,
    isActive: activeTopicId === topic.id,
    ariaLabel: `${topic.label}, ${band}`
  });
}

/**
 * One path row on the `/discovery` Paths tab: the path's artwork in the icon
 * slot as a disc wearing the orbit's own state overlays (progress ring, check
 * or padlock badge — `pathDisc`), its name over an optional description line
 * and the metadata line, then the permanent status slot and the chevron.
 *
 * Presentation-only by contract — it does no date formatting, no partner
 * resolution and no percent math. The caller (`DiscoverySection`) resolves all
 * of that into the view model, because viewer identity, partner names and
 * disclosure obligations are not available here.
 *
 * The status label is folded into the `aria-label` so a screen reader hears
 * "Continuing Your Journey, Waiting on Laurel" rather than the bare name.
 *
 * Never emits `discovery-topic-row` or `data-topic-id`: five JS selectors and
 * four CSS rules key on that hook for the Progress tab, and a path row
 * claiming it would corrupt them.
 *
 * @param {Object} vm - Row view model
 * @param {string} vm.id - Path id
 * @param {string} vm.name - Path name (plain text)
 * @param {string} vm.imageUrl - Artwork URL; falsy renders the neutral disc
 * @param {{kind: string, label: string}|null} vm.status - Status chip, or null for none
 * @param {number|null} vm.ringPercent - Completion percent for the tile's
 *   progress ring, or null for none (0 and 100 draw none either way — see
 *   `pathDisc`)
 * @param {'check'|'lock'|null} vm.badge - Rim badge on the tile, or null
 * @param {string} [vm.description=''] - The path in its own words, as plain
 *   text, on its own line between the title and the metadata line; omitted
 *   entirely when empty
 * @param {Array<{text: string}>} [vm.summaryParts] - The metadata line as
 *   clauses; each `text` is escaped here and joined with the shared separator.
 *   Falls back to `vm.summary` when absent
 * @param {string} vm.summary - The same line as plain text — the fallback, and
 *   what non-visual consumers read
 * @param {boolean} vm.dimmed - Not-built path — desaturated artwork
 * @param {boolean} vm.workable - Whether the path can be worked (consumed by the panel, not here)
 * @param {string} [vm.badgeHtml=''] - Trusted HTML for a notification badge,
 *   rendered inside the title row immediately after the path name (e.g.
 *   `window.badgeService.renderCardIndicator(...)`); omitted entirely when empty
 * @param {Object} [opts]
 * @param {string|null} [opts.activePathId=null] - Path whose detail is
 *   currently open (adds `session-card--active` + `aria-expanded`)
 * @returns {string} HTML string
 */
export function renderPathRow(vm, { activePathId = null } = {}) {
  const status = vm.status || null;
  const dimCls = vm.dimmed ? ' discovery-path-row--dimmed' : '';

  // A broken asset must leave a neutral disc, not a broken-image glyph; the
  // fallback class is added by the img's own error, so no listener wiring is
  // needed for markup that is re-rendered wholesale. The tile is the orbit
  // disc in miniature: the same ring and rim badge, from the same renderer.
  const artHtml = vm.imageUrl
    ? `<img class="discovery-path-row__art" src="${escapeHtml(vm.imageUrl)}" alt="" onerror="this.classList.add('is-missing')">`
    : '<span class="discovery-path-row__art is-missing" aria-hidden="true"></span>';
  const slotHtml = artHtml + discOverlaysHtml({ percent: vm.ringPercent, badge: vm.badge });

  const bodyHtml = vm.description
    ? `<p class="discovery-path-row__desc">${escapeHtml(vm.description)}</p>`
    : '';

  // Words only: the tile's badge is the glyph.
  const metaHtml = status
    ? `<span class="discovery-path-row__status" data-status-kind="${escapeHtml(status.kind)}">${escapeHtml(status.label)}</span>`
    : '';

  const parts = Array.isArray(vm.summaryParts) ? vm.summaryParts : null;
  const summaryHtml = parts ? parts.map(part => escapeHtml(part.text)).join(' · ') : '';

  return renderNavRow({
    rowClass: `discovery-path-row${dimCls}`,
    idAttr: 'data-path-id',
    id: vm.id,
    slotHtml,
    slotClass: 'path-disc',
    title: vm.name,
    summary: vm.summary,
    summaryHtml,
    bodyHtml,
    metaHtml,
    titleSuffixHtml: vm.badgeHtml || '',
    isActive: activePathId === vm.id,
    ariaLabel: status ? `${vm.name}, ${status.label}` : vm.name
  });
}

/**
 * Wire the topic-row list's interactions: click / Enter / Space opens a
 * topic, and row hover spotlights the matching chart slice (the reverse
 * direction — wedge hover → row hot — rides the chart's own `onWedgeHover`,
 * which never fires from `spotlight()`, so the two channels can't echo).
 * Delegated from the stable list container so the handlers survive in-place
 * row updates.
 *
 * The defaults describe the Progress tab's topic rows; the Paths tab passes
 * `rowSelector: '.discovery-path-row[data-path-id]'`, `idKey: 'pathId'` and
 * its own orbit as the visual.
 *
 * @param {HTMLElement} list - The row-list container
 * @param {Object} opts
 * @param {(id: string) => void} opts.onOpen - Row activation handler
 * @param {() => (Object|null)} [opts.getChart] - Lazily resolves the visual
 *   (it may mount after binding); omit for a chartless list
 * @param {string} [opts.rowSelector='.discovery-topic-row[data-topic-id]'] - Row selector
 * @param {string} [opts.idKey='topicId'] - `dataset` key holding the row's id
 */
export function bindTopicRows(list, {
  onOpen,
  getChart = () => null,
  rowSelector = '.discovery-topic-row[data-topic-id]',
  idKey = 'topicId'
}) {
  list.addEventListener('click', (e) => {
    const row = e.target.closest(rowSelector);
    if (row) onOpen(row.dataset[idKey]);
  });
  list.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const row = e.target.closest(rowSelector);
    if (!row) return;
    // Space would otherwise scroll the page.
    e.preventDefault();
    onOpen(row.dataset[idKey]);
  });
  list.addEventListener('pointerover', (e) => {
    const row = e.target.closest(rowSelector);
    if (row) getChart()?.spotlight(row.dataset[idKey]);
  });
  list.addEventListener('pointerout', (e) => {
    const row = e.target.closest(rowSelector);
    if (!row) return;
    // Ignore moves that stay within the same row; only clear on true exit.
    if (e.relatedTarget && row.contains(e.relatedTarget)) return;
    getChart()?.spotlight(null);
  });
}

/**
 * Toggle the `session-card--hot` wash on exactly the row matching a hovered
 * slice of the accompanying visual (the visual→row direction of the hover
 * sync).
 *
 * @param {HTMLElement} list - The row-list container
 * @param {string|null} id - Hovered row id, or null to clear
 * @param {Object} [opts]
 * @param {string} [opts.rowSelector='.discovery-topic-row[data-topic-id]'] - Row selector
 * @param {string} [opts.idKey='topicId'] - `dataset` key holding the row's id
 */
export function setTopicRowHot(list, id, {
  rowSelector = '.discovery-topic-row[data-topic-id]',
  idKey = 'topicId'
} = {}) {
  list.querySelectorAll(rowSelector).forEach(row => {
    row.classList.toggle('session-card--hot', id != null && row.dataset[idKey] === id);
  });
}
